import { useTranslation } from 'react-i18next';
import { Anchor, Group, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { contextsTestIds } from '@contexts/testids';
import { BILLING_CONTACT_MAILTO } from '@utils/billingContact';
import useAuth from './useAuth';
import styles from './SuspensionBanner.module.css';

/**
 * TV1-1783 — persistent layout-level banner shown while the tenant's billing
 * account is suspended for non-payment. Mounted in `App.tsx` above
 * `<RouterProvider>` (next to `<ImpersonationBanner/>`) so it appears on every
 * route, and rendered as the header of the full-screen block content.
 *
 * Renders `null` when the account is not suspended, so active users get zero
 * DOM impact.
 *
 * v1 has NO functional self-service pay button — the tenant-facing
 * Subscriptions/pay screen is not designed yet (deferred to a new ticket). The
 * copy directs the user to contact billing (mailto), which is the product
 * "starting point" per the resolved OQ-6/OQ-7 decisions.
 */
export function SuspensionBanner() {
  const { t } = useTranslation( 'subscription' );
  const auth = useAuth();

  if ( !auth ) return null;
  if ( auth.user?.suspensionStatus !== 'suspended' ) return null;

  return (
    <Group
      justify="center"
      align="center"
      px="md"
      py="xs"
      gap="xs"
      wrap="nowrap"
      data-testid={contextsTestIds.suspensionBanner}
      className={styles.banner}
    >
      <IconAlertTriangle size={16} />
      <Text fz="sm" data-testid={contextsTestIds.suspensionBannerMessage}>
        {t( 'suspension_banner_message' )}
      </Text>
      <Anchor
        // Shared with the `/subscription` cancellation notice — one contact route,
        // one literal (`utils/billingContact.ts`).
        href={BILLING_CONTACT_MAILTO}
        fz="sm"
        fw={600}
        data-testid={contextsTestIds.suspensionBannerContactLink}
      >
        {t( 'suspension_banner_contact' )}
      </Anchor>
    </Group>
  );
}

export default SuspensionBanner;
